import { Archive, Inbox, Users } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { authorNameFor } from "../api/use-review-comments"
import { type TypingViewer } from "../api/use-review-live"
import { AuthorSide, type Finding, type FindingId, type Message } from "../data/review-data"

import { MessageBubble } from "./message-bubble"

/**
 * Messages sheet.
 *
 * Backend contract (see api/routes_comments.py): a comment carries only
 * body, finding_id (optional), and a server-derived author_side. There is
 * no internal/client-visible flag — every comment on a review is equally
 * visible to every side that has access. Unread state is tracked per side,
 * not per user, so marking-read only fires when the panel opens.
 *
 * That is a deliberately narrow surface. If a reviewer needs a private
 * note, the product answer is a separate resource (a "reviewer note"),
 * not a boolean smuggled onto a shared message — because a mis-set
 * boolean is how confidential text leaks to a client.
 */

export interface MessagesSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  messages: readonly Message[]
  findings: Finding[]
  activeFindingId: FindingId | null
  /** True when the review has an engagement — flavours the visibility line. */
  clientAttached: boolean
  /** Archived reviews still take comments; the reader should know it is closed. */
  archived: boolean
  onFocusFinding: (id: FindingId) => void
  /**
   * Omitted, with `onMarkRead`, for a reader who may read the conversation but
   * not take part (a viewer). The sheet then opens read-only: no composer, and
   * nothing is marked read — marking read is a write the server refuses them,
   * and read state is per side, so it would clear the reviewer's badge.
   */
  onSend?: ((input: { body: string; findingId: FindingId | null }) => void) | undefined
  /** Resend one whose send failed. The failed message keeps its text until it lands. */
  onRetry: (message: Message) => void
  /**
   * Role labels composing right now, from the live socket. Roles rather than
   * names, as presence is: a client contact reads this same channel, and staff
   * identity is not disclosed to the counterparty.
   */
  typing?: readonly TypingViewer[]
  /** Called as the reviewer composes, so other viewers see a typing line. */
  onTyping?: () => void
  onMarkRead?: ((findingId: FindingId | null) => void) | undefined
}

type Scope = "all" | "general" | FindingId

export function MessagesSheet({
  open,
  onOpenChange,
  messages,
  findings,
  activeFindingId,
  clientAttached,
  archived,
  onFocusFinding,
  onSend,
  onRetry,
  typing = [],
  onTyping,
  onMarkRead,
}: MessagesSheetProps): JSX.Element {
  const [scope, setScope] = React.useState<Scope>(activeFindingId ?? "all")
  const [body, setBody] = React.useState("")
  const endRef = React.useRef<HTMLLIElement>(null)

  React.useEffect(() => {
    if (activeFindingId) setScope(activeFindingId)
  }, [activeFindingId])

  // Mark read when the sheet opens or the scope changes, and only then. The callback is read
  // through a ref: callers pass a fresh arrow every render, and as a dependency it re-fired the
  // POST on each parent re-render (including the one its own success causes) while the sheet was
  // open. Whether there is a callback at all still counts, since a viewer has none.
  const onMarkReadRef = React.useRef(onMarkRead)
  React.useLayoutEffect(() => {
    onMarkReadRef.current = onMarkRead
  })
  const canMarkRead = onMarkRead !== undefined
  React.useEffect(() => {
    if (!open || !canMarkRead) return
    onMarkReadRef.current?.(scope !== "all" && scope !== "general" ? scope : null)
  }, [scope, open, canMarkRead])

  const readOnly = onSend === undefined

  const filtered = React.useMemo(() => {
    if (scope === "all") return messages
    if (scope === "general") return messages.filter((m) => m.findingId === null)
    return messages.filter((m) => m.findingId === scope)
  }, [messages, scope])

  const lastMessageId = filtered[filtered.length - 1]?.id ?? null

  React.useLayoutEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" })
  }, [lastMessageId])

  const send = () => {
    const trimmed = body.trim()
    if (trimmed.length === 0) return
    if (!onSend) return
    const findingId = scope !== "all" && scope !== "general" ? scope : null
    onSend({ body: trimmed, findingId })
    setBody("")
  }

  const activeFindingChip =
    scope !== "all" && scope !== "general" ? (findings.find((f) => f.id === scope) ?? null) : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        size="md"
        title="Messages"
        description="Everyone on this review sees these messages. There is no private mode."
        maximizable
      >
        <div className="flex h-full min-h-0 flex-1 flex-col">
          <ScopePills scope={scope} onChange={setScope} findings={findings} messages={messages} />

          {activeFindingChip && (
            <AnchorCard
              finding={activeFindingChip}
              onJump={() => onFocusFinding(activeFindingChip.id)}
            />
          )}

          <div className="min-h-0 flex-1 overflow-auto px-5 py-5">
            {filtered.length === 0 ? (
              <EmptyThread scope={scope} readOnly={readOnly} />
            ) : (
              <ul className="space-y-4">
                {filtered.map((m, i) => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    showDayRule={i === 0 || !isSameDay(filtered[i - 1]!.at, m.at)}
                    onRetry={onRetry}
                  />
                ))}
                <TypingLine typing={typing} />
                <li ref={endRef} aria-hidden="true" />
              </ul>
            )}
          </div>

          {readOnly ? (
            // Said, not just absent: a missing box reads as a broken sheet.
            <p className="shrink-0 border-t border-border-soft bg-surface px-5 py-3.5 text-caption text-text-3">
              You can read this conversation. Replying is for the review team and the client.
            </p>
          ) : (
            <Composer
              body={body}
              onBodyChange={setBody}
              clientAttached={clientAttached}
              archived={archived}
              {...(onTyping ? { onTyping } : {})}
              activeFindingChip={activeFindingChip}
              onSend={send}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Scope pill row. Three canonical scopes lead ("Everything", "General",
 * and — when applicable — the currently-focused finding), then per-finding
 * pills scroll horizontally so a review with many findings does not push
 * the composer down.
 */
function ScopePills({
  scope,
  onChange,
  findings,
  messages,
}: {
  scope: Scope
  onChange: (s: Scope) => void
  findings: Finding[]
  messages: readonly Message[]
}): JSX.Element {
  /*
     Unread where there is unread, else the total.

     A chip reading "4" when all four have been read sends the reader into a
     thread with nothing new in it, and the badge stops meaning anything. The
     server computes `unread` per side — own messages never count, and reading
     a thread does not clear it for the other side — so this only tallies a
     flag it already decided.
  */
  const countBy = React.useMemo(() => {
    const tally = (subset: readonly Message[]): number => {
      const unread = subset.filter((m) => m.unread).length
      return unread > 0 ? unread : subset.length
    }
    const map = new Map<FindingId | "general" | "all", number>()
    map.set("all", tally(messages))
    map.set("general", tally(messages.filter((m) => m.findingId === null)))
    for (const f of findings) {
      map.set(f.id, tally(messages.filter((m) => m.findingId === f.id)))
    }
    return map
  }, [messages, findings])

  /** Which chips carry something unread, so the badge can say so. */
  const hasUnread = React.useMemo(() => {
    const set = new Set<FindingId | "general" | "all">()
    if (messages.some((m) => m.unread)) set.add("all")
    if (messages.some((m) => m.findingId === null && m.unread)) set.add("general")
    for (const f of findings) {
      if (messages.some((m) => m.findingId === f.id && m.unread)) set.add(f.id)
    }
    return set
  }, [messages, findings])

  return (
    <div className="shrink-0 border-b border-border-soft px-5 py-3">
      <div className="flex items-center gap-1.5">
        <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">Filter</span>
        <span aria-hidden="true" className="h-3 w-px bg-border" />
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Pill
            active={scope === "all"}
            onClick={() => onChange("all")}
            count={countBy.get("all")}
            unread={hasUnread.has("all")}
          >
            Everything
          </Pill>
          <Pill
            active={scope === "general"}
            onClick={() => onChange("general")}
            count={countBy.get("general")}
            unread={hasUnread.has("general")}
          >
            General
          </Pill>
          <span aria-hidden="true" className="mx-0.5 h-3 w-px shrink-0 bg-border" />
          {findings.map((f) => (
            <Pill
              key={f.id}
              active={scope === f.id}
              onClick={() => onChange(f.id)}
              title={f.claim}
              count={countBy.get(f.id)}
              unread={hasUnread.has(f.id)}
              index={f.number}
            >
              {chipLabel(f)}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * What a finding's filter chip says.
 *
 * The rule id alone is not an identifier a reader can use: one review commonly
 * carries four TXT-01s, which renders four chips reading `TXT-01` in a strip
 * that already scrolls sideways. The section is what tells them apart — it is
 * also how the AI's own message names them ("Wording divergence in
 * precautionary") — so the two together are the shortest label that is
 * actually distinguishing.
 *
 * Falls back to the rule id alone where the pipeline could not place the
 * finding, which is honest: an invented section would be worse than a
 * repeated label.
 */
export function chipLabel(finding: Finding): string {
  if (!finding.sectionId) return finding.ruleId
  return `${finding.ruleId} · ${humanSection(finding.sectionId)}`
}

/** `physical_chemical_hazards` → `physical chemical hazards`. */
function humanSection(sectionId: string): string {
  return sectionId.replace(/_/g, " ")
}

/**
 * Who is composing, at the foot of the thread where chat puts it.
 *
 * Announced politely rather than assertively: this is ambient information, and
 * a screen reader interrupting a reader mid-message to say someone is typing
 * would be worse than silence.
 */
/**
 * How to name whoever is composing.
 *
 * `Amelia Brooks (Reviewer)` where the server sent a name, which it does only
 * for staff readers — a client's socket has the name stripped, so they see
 * `Reviewer` and never learn which person is replying.
 *
 * The role is collapsed to a side and labelled by `authorNameFor`, the same
 * function the bubbles use. The wire sends a role (`admin`, `sme`), and the
 * conversation speaks in sides (`Reviewer`, `Client`); naming a role here that
 * appears nowhere else on the screen would be a third vocabulary.
 */
export function describeTypist(typist: TypingViewer | undefined): string {
  if (!typist) return "Someone"
  const side = typist.role === AuthorSide.CLIENT ? AuthorSide.CLIENT : AuthorSide.REVIEWER
  const label = authorNameFor(side)
  return typist.name ? `${typist.name} (${label})` : label
}

function TypingLine({ typing }: { typing: readonly TypingViewer[] }): JSX.Element | null {
  if (typing.length === 0) return null
  const who =
    typing.length === 1
      ? `${describeTypist(typing[0])} is typing`
      : `${String(typing.length)} people are typing`
  return (
    <li aria-live="polite" className="px-0.5 text-caption italic text-text-3">
      {who}…
    </li>
  )
}

interface PillProps {
  active: boolean
  onClick: () => void
  title?: string
  count?: number | undefined
  /** The count is unread rather than a total — shown in the unread colour. */
  unread?: boolean | undefined
  index?: number | undefined
  children: React.ReactNode
}

function Pill({ active, onClick, title, count, unread, index, children }: PillProps): JSX.Element {
  const hasCount = typeof count === "number" && count > 0
  const ref = React.useRef<HTMLButtonElement>(null)

  /*
     Bring the selected chip into view.

     The strip scrolls horizontally and a review carries a chip per finding, so
     the active one is often past the right edge — selected, and invisible until
     the reader goes looking for it. That holds whoever did the selecting:
     opening the sheet on a finding, or tapping a badge in the rail.

     `inline: "nearest"` so a chip already on screen does not shunt the strip
     around, and `block: "nearest"` so this never scrolls the sheet itself.
  */
  React.useEffect(() => {
    if (!active) return
    ref.current?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" })
  }, [active])

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={cn(
        "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium transition-colors duration-120 ease-out",
        active
          ? "bg-text text-[color:var(--surface)]"
          : "bg-transparent text-text-2 hover:bg-surface-sunken hover:text-text",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      {typeof index === "number" && (
        <span
          className={cn(
            "font-id text-[10px] font-semibold tabular-nums",
            active ? "text-[color:var(--surface)]/60" : "text-text-3",
          )}
        >
          {index}
        </span>
      )}
      <span>{children}</span>
      {hasCount && (
        <span
          className={cn(
            "font-id text-[10.5px] tabular-nums",
            unread
              ? active
                ? "font-semibold text-[color:var(--surface)]"
                : "font-semibold text-err"
              : active
                ? "text-[color:var(--surface)]/60"
                : "text-text-3",
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

/** Anchor card. Tapping it re-focuses the finding's overlay on the PDF. */
function AnchorCard({ finding, onJump }: { finding: Finding; onJump: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onJump}
      className="group mx-5 mt-3 flex shrink-0 items-start gap-3 rounded-[10px] border border-border bg-surface p-3 text-left transition-colors duration-120 ease-out hover:border-border-strong hover:bg-surface-sunken/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
    >
      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-text font-id text-[10.5px] font-semibold tabular-nums text-[color:var(--surface)]">
        {finding.number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-id text-[11px] font-semibold uppercase tracking-[0.06em] text-text">
            {finding.ruleId}
          </span>
          <span className="font-id text-[9.5px] uppercase tracking-[0.08em] text-text-3 group-hover:text-text-2">
            Tap to jump
          </span>
        </span>
        <span className="mt-1 line-clamp-2 block text-caption leading-[1.45] text-text-2">
          {finding.claim}
        </span>
      </span>
    </button>
  )
}

function EmptyThread({ scope, readOnly }: { scope: Scope; readOnly: boolean }): JSX.Element {
  const label =
    scope === "all"
      ? "on this review"
      : scope === "general"
        ? "in the general thread"
        : "on this finding"
  return (
    <div className="flex h-full items-center justify-center py-6">
      <div className="flex max-w-[30ch] flex-col items-center gap-2 text-center">
        <span className="grid size-8 place-items-center rounded-full bg-surface-sunken text-text-3">
          <Inbox className="size-4" aria-hidden="true" />
        </span>
        <p className="text-body-sm font-medium text-text">Nothing yet {label}</p>
        {!readOnly && (
          <p className="text-caption leading-[1.5] text-text-3">
            Start the conversation below. Everyone on this review will see it.
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Composer. Visibility is stated as a fact ("everyone on this review sees
 * this") rather than as a toggle, because the backend has no private
 * message concept. Making that a toggle would sell a control the server
 * cannot honour — and a mis-set toggle is precisely how confidential text
 * leaks to a client.
 */
function Composer({
  body,
  onBodyChange,
  onTyping,
  clientAttached,
  archived,
  activeFindingChip,
  onSend,
}: {
  body: string
  onBodyChange: (v: string) => void
  /** Fired as the reviewer types; throttled by the caller, not here. */
  onTyping?: () => void
  clientAttached: boolean
  archived: boolean
  activeFindingChip: Finding | null
  onSend: () => void
}): JSX.Element {
  const canSend = body.trim().length > 0
  return (
    <div className="shrink-0 border-t border-border-soft bg-surface px-5 pb-4 pt-3">
      <div>
        <Textarea
          minRows={2}
          maxRows={5}
          value={body}
          onChange={(e) => {
            onBodyChange(e.currentTarget.value)
            // Throttled upstream in `useTypingSignal`, so every keystroke may
            // call this without flooding the socket.
            onTyping?.()
          }}
          placeholder={
            activeFindingChip ? `Reply on ${activeFindingChip.ruleId}…` : "Write a message…"
          }
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault()
              onSend()
            }
          }}
          className="resize-none border-border-soft bg-surface-sunken/40 px-3.5 py-2.5 text-body-sm shadow-none placeholder:text-text-3 focus-visible:border-border-strong focus-visible:bg-surface focus-visible:outline-none focus-visible:ring-0"
        />
        <div className="mt-2 flex items-center gap-3">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-caption text-text-3">
            {/* Archived first: a closed review still takes comments, deliberately —
                the record has to stay answerable to a later question — but the
                reader should not have to infer that from silence. */}
            {archived ? (
              <>
                <Archive className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">Archived — the conversation stays open</span>
              </>
            ) : clientAttached ? (
              <>
                <Users className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">Visible to the client</span>
              </>
            ) : (
              <>
                {/* The condition, not a mode. "Internal only" beside a padlock
                    reads as a privacy control, and this panel has none: the
                    backend carries no internal/client-visible flag, so a message
                    written here is visible to the client the moment a contact is
                    attached to the engagement. Saying why nobody can see it yet
                    is the difference between a fact and a promise we cannot
                    keep. */}
                <Users className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  No client contact yet — they will see this once one is added
                </span>
              </>
            )}
          </span>
          <span aria-hidden="true" className="flex-1" />
          <kbd className="hidden shrink-0 font-id text-[10.5px] text-text-3 md:inline">⌘⏎</kbd>
          <Button
            variant="primary"
            size="md"
            onClick={onSend}
            disabled={!canSend}
            aria-label="Send message"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  )
}

function isSameDay(a: string, b: string): boolean {
  return isSameDayDate(new Date(a), new Date(b))
}

function isSameDayDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
